// Activity & Error Logs — admin view of captured user activity and client errors
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessAdmin } from '../utils/roles';
import { fetchActivityLogs } from '../firebase/firestoreService';
import { LOG_TYPE, LOG_CATEGORY } from '../firebase/activityLog';
import '../styles/ActivityLog.css';

const TIMEFRAMES = [
  { value: 1, label: 'Today' },
  { value: 3, label: 'Past 3 Days' },
  { value: 7, label: 'Past Week' },
  { value: 30, label: 'Past Month' },
  { value: 90, label: 'Past Quarter' },
];

const CATEGORY_LABELS = {
  [LOG_CATEGORY.VOLUNTEER_CHECKIN]: 'Volunteer Check-In',
  [LOG_CATEGORY.GUEST_CHECKIN]: 'Guest Check-In',
  [LOG_CATEGORY.CHECKOUT]: 'Check-Out',
  [LOG_CATEGORY.AUTH]: 'Authentication',
  [LOG_CATEGORY.ADMIN]: 'Admin',
  [LOG_CATEGORY.APP]: 'Application',
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds !== undefined) return new Date(value.seconds * 1000);
  if (value._seconds !== undefined) return new Date(value._seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
};

const ActivityLogs = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const [timeframe, setTimeframe] = useState(1);
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    getUserRole(user.uid).then((role) => {
      if (!canAccessAdmin(role)) {
        navigate('/dashboard');
      } else {
        setAuthorized(true);
      }
    });
  }, [user, navigate]);

  const load = useCallback(async (days) => {
    try {
      setLoading(true);
      setError('');

      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const start = new Date();
      start.setDate(start.getDate() - (days - 1));
      start.setHours(0, 0, 0, 0);

      setEntries(await fetchActivityLogs({ startDate: start, endDate: end, limitCount: 1000 }));
    } catch (err) {
      console.error('Error loading activity logs:', err);
      setError(`Failed to load activity logs: ${err.message || 'Please try again.'}`);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authorized) load(timeframe);
  }, [authorized, timeframe, load]);

  const users = useMemo(() => {
    const set = new Set(entries.map(e => e.userName || e.userEmail).filter(Boolean));
    return Array.from(set).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (userFilter !== 'all' && (e.userName || e.userEmail) !== userFilter) return false;
      if (deviceFilter !== 'all' && e.device?.formFactor !== deviceFilter) return false;
      if (!term) return true;
      return (
        (e.action || '').toLowerCase().includes(term) ||
        (e.message || '').toLowerCase().includes(term) ||
        (e.errorMessage || '').toLowerCase().includes(term) ||
        (e.userName || '').toLowerCase().includes(term) ||
        (e.route || '').toLowerCase().includes(term)
      );
    });
  }, [entries, typeFilter, categoryFilter, userFilter, deviceFilter, search]);

  const stats = useMemo(() => ({
    total: filtered.length,
    errors: filtered.filter(e => e.type === LOG_TYPE.ERROR).length,
    failures: filtered.filter(e => e.status === 'failure' || e.status === 'partial').length,
    users: new Set(filtered.map(e => e.userId).filter(Boolean)).size,
  }), [filtered]);

  const exportCsv = () => {
    const rows = [
      ['Time', 'Type', 'Category', 'Action', 'Status', 'Message', 'User', 'Device', 'Browser', 'Viewport', 'Route', 'App Version', 'Error Code', 'Error Message'],
      ...filtered.map((e) => [
        formatDateTime(e.createdAt),
        e.type || '', e.category || '', e.action || '', e.status || '',
        e.message || '',
        e.userName || e.userEmail || '',
        e.device?.formFactor || '', e.device?.browser || '', e.device?.viewport || '',
        e.route || '', e.appVersion || '',
        e.errorCode || '', e.errorMessage || '',
      ]),
    ];
    const csv = rows
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authorized) {
    return <div className="loading"><div className="spinner"></div><p>Loading...</p></div>;
  }

  return (
    <div className="page-container activity-log-page">
      <div className="admin-header-nav">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="admin-title">Activity &amp; Error Logs</h1>
      </div>

      <p className="activity-log-intro">
        What users did in the app and what failed on their device. Entries are written
        from the browser, so they cover the client-side steps that never reach the server
        logs — including which device and connection the operator was on.
      </p>

      {error && <div className="activity-log-banner error">{error}</div>}

      <div className="activity-log-controls">
        <div className="control-group">
          <label htmlFor="al-timeframe">Timeframe</label>
          <select
            id="al-timeframe"
            className="control-select"
            value={timeframe}
            onChange={(e) => setTimeframe(parseInt(e.target.value, 10))}
            disabled={loading}
          >
            {TIMEFRAMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="al-type">Type</label>
          <select id="al-type" className="control-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All</option>
            <option value={LOG_TYPE.ACTION}>Actions</option>
            <option value={LOG_TYPE.ERROR}>Errors</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="al-category">Area</label>
          <select id="al-category" className="control-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">All Areas</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="al-device">Device</label>
          <select id="al-device" className="control-select" value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)}>
            <option value="all">All Devices</option>
            <option value="desktop">Desktop</option>
            <option value="tablet">Tablet / iPad</option>
            <option value="phone">Phone</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="al-user">User</label>
          <select id="al-user" className="control-select" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
            <option value="all">Anyone</option>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        <div className="control-group grow">
          <label htmlFor="al-search">Search</label>
          <input
            id="al-search"
            type="text"
            className="control-input"
            placeholder="Action, message, route…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label>&nbsp;</label>
          <button className="btn btn-secondary" onClick={exportCsv} disabled={loading || filtered.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="activity-log-stats">
        <div className="activity-stat">
          <div className="activity-stat-number">{stats.total}</div>
          <div className="activity-stat-label">Entries</div>
        </div>
        <div className={`activity-stat ${stats.errors > 0 ? 'bad' : ''}`}>
          <div className="activity-stat-number">{stats.errors}</div>
          <div className="activity-stat-label">Errors</div>
        </div>
        <div className={`activity-stat ${stats.failures > 0 ? 'warn' : ''}`}>
          <div className="activity-stat-number">{stats.failures}</div>
          <div className="activity-stat-label">Failed / Partial Actions</div>
        </div>
        <div className="activity-stat">
          <div className="activity-stat-number">{stats.users}</div>
          <div className="activity-stat-label">Users</div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div><p>Loading activity logs...</p></div>
      ) : filtered.length === 0 ? (
        <p className="activity-log-empty">
          No log entries for this timeframe. Entries start accumulating from the first
          use after this feature was deployed.
        </p>
      ) : (
        <div className="activity-log-table">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Area</th>
                <th>Action</th>
                <th>Status</th>
                <th>Message</th>
                <th>User</th>
                <th>Device</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <React.Fragment key={e.id}>
                  <tr className={e.type === LOG_TYPE.ERROR ? 'row-error' : ''}>
                    <td className="cell-nowrap">{formatDateTime(e.createdAt)}</td>
                    <td>{CATEGORY_LABELS[e.category] || e.category}</td>
                    <td className="cell-mono">{e.action}</td>
                    <td>
                      <span className={`activity-badge ${
                        e.status === 'success' ? 'ok'
                          : e.status === 'failure' ? 'bad'
                            : e.status === 'partial' ? 'warn' : 'neutral'
                      }`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="cell-message" title={e.message || ''}>
                      {e.message || e.errorMessage || '—'}
                    </td>
                    <td>{e.userName || e.userEmail || '—'}</td>
                    <td className="cell-nowrap">
                      {e.device
                        ? `${e.device.formFactor} · ${e.device.browser}`
                        : '—'}
                    </td>
                    <td>
                      <button
                        className="activity-expand-btn"
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      >
                        {expanded === e.id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr className="activity-detail-row">
                      <td colSpan={8}>
                        <div className="activity-detail-grid">
                          <div>
                            <h4>Context</h4>
                            <dl>
                              <dt>Route</dt><dd>{e.route || '—'}</dd>
                              <dt>App version</dt><dd>{e.appVersion || '—'}</dd>
                              <dt>Client time</dt><dd>{e.clientTime || '—'}</dd>
                              <dt>Viewport</dt><dd>{e.device?.viewport || '—'}</dd>
                              <dt>Screen</dt><dd>{e.device?.screen || '—'}</dd>
                              <dt>Platform</dt><dd>{e.device?.platform || '—'}</dd>
                              <dt>Online</dt><dd>{e.device ? String(e.device.online) : '—'}</dd>
                              <dt>Connection</dt><dd>{e.device?.connection || '—'}</dd>
                              <dt>Time zone</dt><dd>{e.device?.timeZone || '—'}</dd>
                              <dt>User agent</dt><dd className="wrap">{e.device?.userAgent || '—'}</dd>
                            </dl>
                          </div>
                          <div>
                            <h4>Details</h4>
                            {e.details
                              ? <pre className="activity-detail-json">{JSON.stringify(e.details, null, 2)}</pre>
                              : <p className="activity-detail-empty">No structured details.</p>}
                            {e.errorMessage && (
                              <>
                                <h4>Error</h4>
                                <dl>
                                  <dt>Name</dt><dd>{e.errorName || '—'}</dd>
                                  <dt>Code</dt><dd>{e.errorCode || '—'}</dd>
                                  <dt>Message</dt><dd className="wrap">{e.errorMessage}</dd>
                                </dl>
                                {e.errorStack && (
                                  <pre className="activity-detail-json">{e.errorStack}</pre>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
